/**
 * Categories (R28) and the age check (R33). Dates are 'YYYY-MM-DD' strings, so they compare
 * correctly as text and never shift with the device's time zone.
 */

export const CATEGORY_CODES = [
  'U10B',
  'U10G',
  'U13B',
  'U13G',
  'U16B',
  'U16G',
  'U18B',
  'U18G',
  'SRM',
  'SRW',
] as const;

export type CategoryCode = (typeof CATEGORY_CODES)[number];

/** Month and day of the age cut-off. */
export interface AgeCutoff {
  month: number;
  day: number;
}

/** Owner decision 2026-11-05: ages are counted from 1 January. */
export const JANUARY_FIRST: AgeCutoff = { month: 1, day: 1 };

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));

export function isCategoryCode(value: unknown): value is CategoryCode {
  return typeof value === 'string' && (CATEGORY_CODES as readonly string[]).includes(value);
}

/** The age limit of a category: 16 for U16B, null for Seniors. */
export function categoryAgeLimit(category: CategoryCode): number | null {
  const match = /^U(\d+)/.exec(category);
  return match === null ? null : Number(match[1]);
}

/** U10 to U18. */
export function isYouthCategory(category: CategoryCode): boolean {
  return categoryAgeLimit(category) !== null;
}

/** Earlier years are typing slips. */
const MIN_BIRTH_YEAR = 1900;

/** True for a real calendar date in 'YYYY-MM-DD' form, from 1900, that is not after `today`. */
export function isValidBirthDate(value: unknown, today: string): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match === null) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  const real =
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
  return real && year >= MIN_BIRTH_YEAR && value <= today;
}

/**
 * The earliest birth date a category allows in a year, or null when there is no limit.
 * U16 in 2026 → '2010-01-01'.
 */
export function earliestBirthDate(
  category: CategoryCode,
  year: number,
  cutoff: AgeCutoff = JANUARY_FIRST,
): string | null {
  const limit = categoryAgeLimit(category);
  return limit === null ? null : `${year - limit}-${pad2(cutoff.month)}-${pad2(cutoff.day)}`;
}

/** True when a player born on `birthDate` is young enough for the category that year. */
export function fitsCategory(
  category: CategoryCode,
  birthDate: string,
  year: number,
  cutoff: AgeCutoff = JANUARY_FIRST,
): boolean {
  const earliest = earliestBirthDate(category, year, cutoff);
  return earliest === null || birthDate >= earliest;
}
